/**
 * Gardener progression: the register, streaks and score.
 *
 * The badges are content and live in `src/content/badges.ts`.
 *
 * All of it is pure logic over plain data — no Three.js, no DOM, no storage.
 * `Storage` persists it and `App` reacts to it, which keeps the rules in one
 * readable place and makes them trivially testable.
 *
 * ## Why a register rather than a leaderboard
 *
 * There is no server here, and adding one to rank strangers against each
 * other would mean collecting names and locations from players — a real
 * privacy obligation for a marginal feature. So the model is a *register*:
 * the roll of gardeners who have planted on **this device**, in the way a
 * visitors' book at a garden gate works. That covers both real cases well: a
 * personal phone (one gardener, their own history and streak) and a shared
 * kiosk or a phone handed round a group (several, ranked against each other,
 * which is where it gets competitive).
 *
 * The shape is deliberately server-ready — every entry is a self-contained
 * record with a stable id — so a hosted board can be layered on later without
 * a migration.
 */

import { dayKey } from '../progression/calendar';
import { RARITY_POINTS, SPECIES, type SpeciesId } from '../content/species';
import { MUTATIONS, type MutationId } from '../content/weather';

/* ------------------------------------------------------------------ *
 * The register
 * ------------------------------------------------------------------ */

/**
 * Where a gardener's turn started.
 *
 * The garden is shared — hand the phone over and the next person inherits a
 * plot someone else planted — so a stamp has to score only what its owner did.
 * Plants sown before `sinceMs`, and facts and badges already banked, belong to
 * whoever came before.
 *
 * The exception is the *first* signature on a device: that gardener built
 * everything standing at the moment they signed, and docking them for signing
 * late would punish exactly the behaviour the prompt encourages. Their
 * baseline is zero.
 */
export interface StampBaseline {
  sinceMs: number;
  facts: number;
  badges: number;
}

export interface VisitorStamp {
  id: string;
  /** What the gardener chose to be called. Never validated as a real name. */
  name: string;
  /** Free text — "Lisbon", "Nairobi", "Manchester". Typed, never geolocated. */
  from: string;
  /** Epoch ms the register was signed. */
  stampedAt: number;
  /** Epoch ms this gardener was last active. */
  lastSeenAt: number;
  trees: number;
  species: number;
  facts: number;
  score: number;
  badges: string[];
  /**
   * Specimens this gardener lifted, and what they were worth.
   *
   * Kept as running totals rather than as a ledger of harvests, because a
   * harvested plant is *gone*: its species and its weather mark cannot be
   * recovered from the garden, and the score is otherwise computed from what
   * is standing. Without these three fields, harvesting a tree would quietly
   * cost the gardener the points they earned growing it — which would make
   * the whole mechanic a trap.
   *
   * Only specimens sown at or after `baseline.sinceMs` are credited, exactly
   * as the standing-plant rule works. Lifting a tree the previous gardener
   * grew frees the bed and pays Seedlings, but scores nothing.
   */
  harvested: number;
  harvestScore: number;
  harvestSpecies: SpeciesId[];
  /**
   * Consecutive-day streak at the moment the stamp was last updated.
   *
   * Snapshotted rather than derived, because the run of visit days belongs to
   * the device — a shared kiosk's streak is not any one gardener's — while
   * the number shown beside a name should be what they had when they played.
   */
  streak: number;
  baseline: StampBaseline;
}

export const MAX_NAME = 24;
export const MAX_FROM = 28;

/** Trim and bound a free-text field before it goes anywhere near storage. */
export function sanitiseField(value: string, max: number): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, max);
}

export function makeStamp(
  name: string,
  from: string,
  now: number,
  streak = 1,
  baseline: StampBaseline = { sinceMs: 0, facts: 0, badges: 0 },
): VisitorStamp {
  return {
    // Not cryptographic — it only has to be unique within one device's roll.
    id: `${now.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
    name: sanitiseField(name, MAX_NAME) || 'Anonymous gardener',
    from: sanitiseField(from, MAX_FROM),
    stampedAt: now,
    lastSeenAt: now,
    trees: 0,
    species: 0,
    facts: 0,
    score: 0,
    badges: [],
    harvested: 0,
    harvestScore: 0,
    harvestSpecies: [],
    streak,
    baseline,
  };
}

/** Highest score first; ties broken by who signed first. */
export function rankRegister(register: readonly VisitorStamp[]): VisitorStamp[] {
  return [...register].sort((a, b) => b.score - a.score || a.stampedAt - b.stampedAt);
}

/* ------------------------------------------------------------------ *
 * Streaks
 * ------------------------------------------------------------------ */

/** Local calendar day, not UTC — a streak should match the player's day. */
export { dayKey };

/** Record today's visit, keeping the tail bounded. */
export function recordVisit(days: readonly string[], now: number): string[] {
  const today = dayKey(now);
  if (days[days.length - 1] === today) return [...days];
  return [...days, today].slice(-60);
}

/**
 * Consecutive days ending today or yesterday.
 *
 * Yesterday counts so that opening the app at 00:05 does not read as a broken
 * streak to someone who played at 23:55. Streaks here reward returning; they
 * never punish, and nothing is lost when one lapses.
 */
export function currentStreak(days: readonly string[], now: number): number {
  if (days.length === 0) return 0;
  const set = new Set(days);
  const DAY = 86_400_000;

  let cursor = now;
  if (!set.has(dayKey(cursor))) {
    cursor -= DAY;
    if (!set.has(dayKey(cursor))) return 0;
  }

  let streak = 0;
  while (set.has(dayKey(cursor))) {
    streak += 1;
    cursor -= DAY;
  }
  return streak;
}

/* ------------------------------------------------------------------ *
 * Score
 * ------------------------------------------------------------------ */

const SPECIES_BONUS = 25;
const FACT_BONUS = 15;
const BADGE_BONUS = 40;

/** One matured specimen, as the scoreboard sees it. */
export interface ScoredPlant {
  species: SpeciesId;
  mutation: MutationId | null;
}

/**
 * A gardener's score.
 *
 * Weighted by rarity — so the scoreboard rewards exactly the trees that
 * drop least often. Facts and badges count too, so reading and returning are worth
 * as much as planting.
 *
 * Mutations multiply. It is what makes a sandstorm worth sitting through
 * rather than merely survived: the storm slows a specimen down and then makes
 * it worth 40% more, so the weather that costs you time is also the weather
 * that pays.
 */
export function pointsFor(plant: ScoredPlant): number {
  const species = SPECIES[plant.species];
  if (!species) return 0;
  const bonus = plant.mutation ? MUTATIONS[plant.mutation].bonus : 1;
  return Math.round(RARITY_POINTS[species.rarity] * bonus);
}

export function scoreFor(
  plants: readonly ScoredPlant[],
  distinctSpecies: number,
  facts: number,
  badges: number,
  /**
   * Points already banked from specimens that have been harvested and no
   * longer exist to be counted. See `VisitorStamp.harvestScore`.
   */
  carried = 0,
): number {
  let total = carried;
  for (const plant of plants) {
    total += pointsFor(plant);
  }
  return (
    total +
    distinctSpecies * SPECIES_BONUS +
    facts * FACT_BONUS +
    badges * BADGE_BONUS
  );
}

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

/** "4m", "2h 15m", "3 days" — short enough for a HUD line. */
export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) {
    const rem = m % 60;
    return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
  }
  const d = Math.round(h / 24);
  return d === 1 ? '1 day' : `${d} days`;
}

/** "20 Aug 2026" — unambiguous across the audiences this ships to. */
export function formatStampDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
